import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Bell } from 'lucide-react';
import './NotificationBell.css';

const API_ROOT = process.env.NODE_ENV === 'production'
  ? (process.env.REACT_APP_API_URL || '')
  : (process.env.REACT_APP_API_URL || 'http://localhost:5001');
const API_BASE_URL = API_ROOT ? `${API_ROOT}/api` : '/api';

function formatWhen(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString();
}

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const rootRef = useRef(null);

  const refreshCount = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_BASE_URL}/notifications/unread-count`);
      setUnread(data.count || 0);
    } catch {
      /* ignore */
    }
  }, []);

  const loadList = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API_BASE_URL}/notifications`, {
        params: { limit: 20 }
      });
      setItems(data || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshCount();
    const timer = setInterval(refreshCount, 30000);
    return () => clearInterval(timer);
  }, [refreshCount]);

  useEffect(() => {
    if (!open) return undefined;
    loadList();
    const onDoc = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open, loadList]);

  const markAllRead = async () => {
    try {
      await axios.post(`${API_BASE_URL}/notifications/read-all`);
      setUnread(0);
      setItems((prev) => prev.map((row) => ({ ...row, read_at: row.read_at || new Date().toISOString() })));
    } catch {
      /* ignore */
    }
  };

  const openItem = async (item) => {
    if (!item.read_at) {
      try {
        await axios.patch(`${API_BASE_URL}/notifications/${item.id}/read`);
        setUnread((n) => Math.max(0, n - 1));
        setItems((prev) =>
          prev.map((row) =>
            row.id === item.id ? { ...row, read_at: new Date().toISOString() } : row
          )
        );
      } catch {
        /* ignore */
      }
    }
    setOpen(false);
    if (item.link) {
      window.location.href = item.link;
    }
  };

  return (
    <div className="notif-bell" ref={rootRef}>
      <button
        type="button"
        className="btn btn-outline notif-bell-btn"
        aria-label="Notifications"
        onClick={() => setOpen((v) => !v)}
      >
        <Bell size={16} />
        {unread > 0 && <span className="notif-bell-count">{unread > 99 ? '99+' : unread}</span>}
      </button>
      {open && (
        <div className="notif-panel" role="dialog" aria-label="Notifications">
          <div className="notif-panel-head">
            <strong>Notifications</strong>
            <button type="button" className="btn btn-link" onClick={markAllRead} disabled={!unread}>
              Mark all read
            </button>
          </div>
          <div className="notif-panel-body">
            {loading && <p className="notif-empty">Loading…</p>}
            {!loading && items.length === 0 && (
              <p className="notif-empty">No notifications yet.</p>
            )}
            {!loading &&
              items.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className={`notif-item${!item.read_at ? ' is-unread' : ''}`}
                  onClick={() => openItem(item)}
                >
                  <span className="notif-item-title">{item.title}</span>
                  {item.body && <span className="notif-item-body">{item.body}</span>}
                  <span className="notif-item-time">{formatWhen(item.created_at)}</span>
                </button>
              ))}
          </div>
        </div>
      )}
    </div>
  );
}
